import * as React from "react";
import * as Immutable from "immutable";
import LookupTableParameterForm from "enterprise/parameters/components/lookup-table/LookupTableParameterForm";
import LookupTableParameter from "views/logic/parameters/LookupTableParameter";
import LookupTableParameterPreview from "enterprise/parameters/components/lookup-table/LookupTableParameterPreview";
import CreateParameterDialog from "enterprise/parameters/creatoractions/CreateParameterDialog";
import ParameterCompletion from "enterprise/parameters/components/searchbar/completions/ParameterCompletion";
import GenericParameterForm from "enterprise/parameters/components/generic/GenericParameterForm";
import GenericParameterInput from "enterprise/parameters/components/generic/GenericParameterInput";
import ValueParameter from "views/logic/parameters/ValueParameter";
import Parameter from "views/logic/parameters/Parameter";
import OptionParameter from "enterprise/parameters/components/option/OptionParameter";
import WatchlistParameter from "watchlist/WatchlistParameter";
import WatchlistParameterForm from "enterprise/parameters/components/watchlist/WatchlistParameterForm";
import WatchlistParameterPreview from "enterprise/parameters/components/watchlist/WatchlistParameterPreview";
import WatchlistQueryParameterEdit from "enterprise/parameters/components/event-definition/WatchlistQueryParameterEdit";
import OptionParameterInput from "enterprise/parameters/components/option/OptionParameterInput";
import OptionParameterForm from "enterprise/parameters/components/option/OptionParameterForm";
import FieldSourceForm from "enterprise/parameters/components/option/FieldSourceForm";
import StaticSourceForm from "enterprise/parameters/components/option/StaticSourceForm";
import { FieldSource, StaticSource } from "enterprise/parameters/components/option/OptionSource";
import validLicensePresent from "license/ValidLicensePresent";
import parameterSearchBarControls from "enterprise/parameters/parameterSearchBarControls";
import { InsertIntoViewValueActionPlugin } from "enterprise/parameters/actions/ValueActionPlugins";
import bindParametersFromQuery from "./hooks/BindParametersFromQuery";
import showParameterFormIfMissingValues from "./hooks/ShowParameterFormIfMissingValues";
import MissingEnterpriseLicense from "./components/MissingEnterpriseLicense";
import ParameterBar from "./components/ParameterBar";
import WidgetEditModeParameterBar from "./components/WidgetEditModeParameterBar";
import QueryValidationParameterDeclaration from "./components/QueryValidationParameterDeclaration";
import CopyParamsToView from "./hooks/CopyParamsToView";
const checkLicenseOrNoop = (fn, fallback = ({ view, executionState }) => Promise.resolve([view, executionState])) => (args) => validLicensePresent() ? fn(args) : fallback(args);
class CheckLicenseOrNoop {
  constructor(completer) {
    this.completer = completer;
  }
  getCompletions(...args) {
    return validLicensePresent() ? this.completer.getCompletions(...args) : [];
  }
}
Parameter.registerSubtype(OptionParameter.type, OptionParameter);
Parameter.registerSubtype(WatchlistParameter.type, WatchlistParameter);
const ParameterBindings = {
  "views.components.searchBar": [() => validLicensePresent() ? parameterSearchBarControls : null],
  "views.elements.validationErrorExplanation": [
    (props) => validLicensePresent() ? /* @__PURE__ */ React.createElement(QueryValidationParameterDeclaration, { ...props }) : null
  ],
  "views.elements.queryBar": [() => validLicensePresent() ? /* @__PURE__ */ React.createElement(ParameterBar, null) : null],
  "views.overrides.widgetEdit": [
    ({ override }) => validLicensePresent() ? /* @__PURE__ */ React.createElement(WidgetEditModeParameterBar, { override }) : null
  ],
  "views.hooks.executingView": [checkLicenseOrNoop(showParameterFormIfMissingValues)],
  "views.hooks.loadingView": [
    checkLicenseOrNoop(
      ({ view, executionState }) => Promise.resolve([view, executionState]),
      async ({ view, executionState }) => {
        var _a, _b;
        const usesParameters = !((_b = (_a = view == null ? void 0 : view.search) == null ? void 0 : _a.parameters) == null ? void 0 : _b.isEmpty());
        const usesFiltersInWidgets = () => view.state.some((viewState) => viewState.widgets.some((widget) => !widget.filters.isEmpty()));
        const usesFiltersInQuery = () => view.search.queries.some((query) => {
          var _a2;
          return !((_a2 = query.filters) != null ? _a2 : Immutable.List()).isEmpty();
        });
        const usesFilters = view.type === "DASHBOARD" ? usesFiltersInWidgets() : usesFiltersInQuery();
        if (usesParameters || usesFilters) {
          throw /* @__PURE__ */ React.createElement(MissingEnterpriseLicense, { usesParameters, usesFilters });
        }
        return [view, executionState];
      }
    ),
    // checkLicenseOrNoop(loadParametersFromView),
    checkLicenseOrNoop(bindParametersFromQuery)
  ],
  "views.hooks.copyWidgetToDashboard": [CopyParamsToView],
  "views.hooks.copyPageToDashboard": [CopyParamsToView],
  "views.completers": [new CheckLicenseOrNoop(ParameterCompletion)],
  "views.requires.provided": ["parameters"],
  valueActions: [InsertIntoViewValueActionPlugin],
  creators: [
    {
      type: "generic",
      title: "Parameter",
      component: CreateParameterDialog,
      useCondition: validLicensePresent
    }
  ],
  parameterDataTypes: [
    {
      type: ValueParameter.type,
      title: "Generic",
      component: GenericParameterInput,
      editComponent: GenericParameterForm
    },
    {
      type: OptionParameter.type,
      title: "Dropdown",
      component: OptionParameterInput,
      editComponent: OptionParameterForm
    },
    {
      type: LookupTableParameter.type,
      title: "Lookup Table",
      component: LookupTableParameterPreview,
      editComponent: LookupTableParameterForm,
      editPermissions: ["lookuptables:read"]
    },
    {
      type: WatchlistParameter.type,
      title: "Watchlist",
      component: WatchlistParameterPreview,
      editComponent: WatchlistParameterForm,
      editPermissions: ["watchlist:read"]
    }
  ],
  eventDefinitionQueryParameterTypes: [
    {
      type: WatchlistParameter.type,
      title: "Watchlist",
      fromJSON: WatchlistParameter.fromJSON.bind(WatchlistParameter),
      validate: (param) => ({
        watchlistId: !param.watchlistId ? "Cannot be empty" : void 0
      }),
      editComponent: WatchlistQueryParameterEdit
    }
  ],
  "views.parameters.option.sources": [
    {
      type: "static",
      title: "Static Values",
      editComponent: StaticSourceForm,
      factory: StaticSource.createDefault
    },
    {
      type: "field",
      title: "Values of field",
      editComponent: FieldSourceForm,
      factory: FieldSource.createDefault
    }
  ]
};
export default ParameterBindings;
